import { PlanTier } from '../graphql/generated/graphql';

export const PlanFeatureKeysOrdered = [
  'users',
  'guests',
  'ai_overview',
  'ai_predict',
  'ai_ask',
  'uploadSize',
  'usageAnalytics',
  'versionHistory',
  'privateTopics',
  'customDomains',
  'customEmbedHosts',
  'integrations',
  'webhooks',
  'api',
  'auditLog',
  'support',
] as const;

export type PlanFeatureKey = typeof PlanFeatureKeysOrdered[number];

type PlanFeatureName = string;
type PlanFeatureValue = string | boolean;
type PlanFeatureRow = {
  name: PlanFeatureName;
  optional?: boolean;
  [PlanTier.Free]: PlanFeatureValue;
  [PlanTier.Startup]: PlanFeatureValue;
  [PlanTier.Business]: PlanFeatureValue;
  [PlanTier.Enterprise]: PlanFeatureValue;
};

const UploadSizeLimitMB = {
  FREE: 10,
  STARTUP: 25,
  BUSINESS: 100,
  ENTERPRISE: Infinity,
};

export const UsageAnalyticsDays = {
  FREE: 30,
  STARTUP: 90,
  BUSINESS: 365,
  ENTERPRISE: Infinity,
};

export const VersionHistoryDays = {
  FREE: 90,
  STARTUP: 365,
  BUSINESS: Infinity,
  ENTERPRISE: Infinity,
};

export const GuestsPerSeat = {
  [PlanTier.Free]: 0,
  [PlanTier.Startup]: 3,
  [PlanTier.Business]: 5,
  [PlanTier.Enterprise]: Infinity,
};

export const PlanFeatures: Record<PlanFeatureKey, PlanFeatureRow> = {
  users: {
    name: 'Users',
    FREE: 'Up to 10',
    STARTUP: 'Unlimited',
    BUSINESS: 'Unlimited',
    ENTERPRISE: 'At least 100',
  },
  guests: {
    name: 'Guests',
    FREE: false,
    STARTUP: '3 per user',
    BUSINESS: '5 per user',
    ENTERPRISE: 'Unlimited',
  },
  ai_overview: {
    name: 'AI Capabilities',
    FREE: 'AI Autofix',
    STARTUP: 'AI Predict',
    BUSINESS: 'AI Ask',
    ENTERPRISE: 'AI Ask',
  },
  ai_ask: {
    name: 'AI Ask',
    optional: true,
    FREE: false,
    STARTUP: false,
    BUSINESS: true,
    ENTERPRISE: 'Advanced',
  },
  ai_predict: {
    name: 'AI Predict',
    optional: true,
    FREE: false,
    STARTUP: true,
    BUSINESS: true,
    ENTERPRISE: true,
  },
  uploadSize: {
    name: 'Attachments',
    optional: true,
    FREE: `${UploadSizeLimitMB.FREE} MB`,
    STARTUP: `${UploadSizeLimitMB.STARTUP} MB`,
    BUSINESS: `${UploadSizeLimitMB.BUSINESS} MB`,
    ENTERPRISE: 'Custom',
  },
  versionHistory: {
    name: 'Version History',
    FREE: `${VersionHistoryDays.FREE} days`,
    STARTUP: `${VersionHistoryDays.STARTUP} days`,
    BUSINESS: 'Unlimited',
    ENTERPRISE: 'Unlimited',
  },
  usageAnalytics: {
    name: 'Usage Analytics',
    FREE: `${UsageAnalyticsDays.FREE} days`,
    STARTUP: `${UsageAnalyticsDays.STARTUP} days`,
    BUSINESS: `${UsageAnalyticsDays.BUSINESS} days`,
    ENTERPRISE: 'Unlimited',
  },
  integrations: {
    name: 'Premium Integrations',
    FREE: false,
    STARTUP: false,
    BUSINESS: '3',
    ENTERPRISE: 'Unlimited',
  },
  privateTopics: {
    name: 'Private & Secret Topics',
    optional: true,
    FREE: false,
    STARTUP: true,
    BUSINESS: true,
    ENTERPRISE: true,
  },
  customDomains: {
    name: 'Custom Domains',
    optional: true,
    FREE: false,
    STARTUP: false,
    BUSINESS: true,
    ENTERPRISE: true,
  },
  customEmbedHosts: {
    name: 'Custom Embed Hosts',
    optional: true,
    FREE: false,
    STARTUP: true,
    BUSINESS: true,
    ENTERPRISE: true,
  },
  webhooks: {
    name: 'Webhooks',
    optional: true,
    FREE: false,
    STARTUP: false,
    BUSINESS: true,
    ENTERPRISE: true,
  },
  api: {
    name: 'API',
    optional: true,
    FREE: false,
    STARTUP: false,
    BUSINESS: true,
    ENTERPRISE: true,
  },
  auditLog: {
    name: 'Audit Logs',
    optional: true,
    FREE: false,
    STARTUP: false,
    BUSINESS: true,
    ENTERPRISE: true,
  },
  support: {
    name: 'Support',
    FREE: 'Basic',
    STARTUP: 'Standard',
    BUSINESS: 'Priority',
    ENTERPRISE: 'Dedicated',
  },
};
